// Per-item pre-PO shipping plan (SO→PO rebuild Round 1, T05 + T07).
//
// The commonsku "Shipping" surface: one row per product line, columns
// Product | Supplier Shipping | Decorator Shipping (summary strings), a per-row
// + select-all checkbox, and a bulk "Edit Selected" action (T07). Each row (or
// the bulk action) opens the two-leg LineItemShippingModal (T06). The section
// UNLOCKS at Client Approved; before that it explains-don't-hides (house
// standard) and links to the approval control. Titled "Shipping plan" with its
// own icon — distinct from a PO's own "Shipping" block so reps don't think they
// lost their work on two similarly-named tools. A line already on a (live) PO
// links "On PO #N →" and stays editable — the tab never locks (2026-10-27);
// it just never goes on a second PO. Data-fetching is
// TanStack Query; brand-locked (brown/rust/cream/peach).
import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  LineItemShippingModal,
  commonShippingRow,
  type LineShippingRow,
} from "@/components/orders/LineItemShippingModal";
import { ShipSplitModal } from "@/components/orders/ShipSplitModal";
import { splitSummary, type SavedSplit, type SplitVariant } from "@/lib/ship-split";

/** Split shipping (Stephanie 2026-10-16): each item's addresses + its sizes. */
interface ShipSplitsResponse {
  ready: boolean;
  splits: Record<string, SavedSplit[]>;
  lines: Record<string, { quantityTotal: number; variants: SplitVariant[] }>;
}

/** A line is selectable for the bulk action iff it is a product line NOT already
 *  covered by a live PO (a covered line's staging is frozen — see coverage in
 *  getLineItemShippingForOrder, which reverts cancelled-PO lines to editable so
 *  they ARE selectable again). Pure so the intent is obvious + testable. */
/** "Client Approved" for a quote — the shipping plan's unlock rule (mirrors
 *  commonsku's "Shipping unlocks at Client Approved"): signed off, paid
 *  upfront, or already past approval. One source for the quote page and the
 *  project's Shipping tab. */
export function isQuoteClientApproved(order: {
  statusStage: string;
  soApprovedAt?: string | null;
  upfrontPaymentStatus?: string | null;
}): boolean {
  return (
    order.soApprovedAt != null ||
    order.upfrontPaymentStatus === "paid" ||
    ["in_production", "shipped", "invoiced", "closed"].includes(order.statusStage)
  );
}

export function isLineBulkSelectable(r: LineShippingRow): boolean {
  // Never locked, even once on a PO (Stephanie 2026-10-27: "the shipping tab
  // should not lock at all").
  return r.serviceType === "product";
}

/** The product label for a row (name → description → "Line N"). Shared by the
 *  table cell and the bulk per-line result summary (D9). */
function rowLabel(r: LineShippingRow): string {
  return (
    r.productSnapshot?.name?.trim() ||
    r.description?.trim() ||
    `Line ${r.lineNumber}`
  );
}

interface LineShippingResponse {
  inHandsDate: string | null;
  projectId: string | null;
  rows: LineShippingRow[];
}

const ROUTING_LABELS: Record<string, string> = {
  client: "Client",
  decorator: "Decorator",
  merchd: "Other",
  other_supplier: "Other supplier",
};

function formatDate(s: string | null): string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s ?? "";
  return new Date(`${s}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// Detailed cells, like commonsku's Shipping page (Stephanie 2026-10-27: "can
// we make this screen more detailed?"): each leg as label / value rows —
// who ships, ship date, ship to, the full address, method, notes — instead
// of one truncated line.
interface ShipSnap {
  label?: string;
  attention?: string | null;
  street1?: string;
  street2?: string | null;
  city?: string;
  stateCode?: string;
  postalCode?: string;
}

/** The item's picture and its logos, so two of the same item with different
 *  logos can be told apart (Stephanie 2026-10-27: "there should also be photos
 *  like commonsku"). Built by the Shipping tab from the quote's lines. */
export interface ShippingLineArt {
  imageUrl: string | null;
  logos: Array<{ fileId: string; location: string | null; name: string | null }>;
}

interface LogoFileMeta {
  original_filename: string;
  is_image: boolean;
  view_url: string | null;
  preview_url?: string | null;
}

function LogoThumb({ fileId, label }: { fileId: string; label: string | null }) {
  // Same cache key as the quote's art tiles, so nothing is fetched twice.
  const { data } = useQuery({
    queryKey: ["file-meta", fileId],
    queryFn: () => api<LogoFileMeta>(`/files/${fileId}`),
    staleTime: 5 * 60 * 1000,
  });
  const pic = data ? (data.is_image && data.view_url ? data.view_url : data.preview_url ?? null) : null;
  return (
    <figure className="w-16" title={[label, data?.original_filename].filter(Boolean).join(" · ")}>
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-brown-100 bg-white">
        {pic ? (
          <img src={pic} alt={data?.original_filename ?? "Logo"} loading="lazy" className="h-full w-full object-contain p-0.5" />
        ) : (
          <span className="px-1 text-center text-[10px] font-semibold text-brown-300">Logo</span>
        )}
      </div>
      {label && <figcaption className="mt-0.5 truncate text-[11px] text-brown-400">{label}</figcaption>}
    </figure>
  );
}

function LineArtStrip({ art }: { art: ShippingLineArt | undefined }) {
  if (!art || (!art.imageUrl && art.logos.length === 0)) return null;
  return (
    <div className="mt-2 flex flex-wrap items-start gap-2">
      {art.imageUrl && (
        <figure className="w-16">
          <div className="h-16 w-16 overflow-hidden rounded-md bg-cream-100">
            <img src={art.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <figcaption className="mt-0.5 text-[11px] text-brown-400">Item</figcaption>
        </figure>
      )}
      {art.logos.map((l, i) => (
        <LogoThumb key={`${l.fileId}-${i}`} fileId={l.fileId} label={l.location} />
      ))}
    </div>
  );
}

function DetailRows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-1 text-[14px] leading-snug">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-semibold text-brown-500">{k}</dt>
          <dd className="text-brown-500">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function shipDateText(date: string | null, firm: boolean): string {
  return `${formatDate(date)}${firm ? " (firm)" : ""}`;
}

function AddressLines({ snap }: { snap: ShipSnap }) {
  const cityLine = [snap.city, snap.stateCode].filter(Boolean).join(", ") + (snap.postalCode ? ` ${snap.postalCode}` : "");
  return (
    <>
      {snap.attention && <span className="block">Attn: {snap.attention}</span>}
      {snap.street1 && <span className="block">{snap.street1}</span>}
      {snap.street2 && <span className="block">{snap.street2}</span>}
      {cityLine.trim() && <span className="block">{cityLine.trim()}</span>}
    </>
  );
}

function SupplierDetail({ r }: { r: LineShippingRow }) {
  if (!r.hasStagedRow) {
    return (
      <td className="px-3 py-3 align-top text-[14px] italic text-brown-300">Not set yet — click Edit</td>
    );
  }
  const snap = (r.supplierShipToAddressSnapshot ?? null) as ShipSnap | null;
  const rows: Array<[string, ReactNode]> = [];
  rows.push(["Name", r.supplierVendorName ?? <span className="italic text-brown-300">Not set</span>]);
  if (r.supplierShipDate) rows.push(["Ship date", shipDateText(r.supplierShipDate, r.supplierShipDateFirm)]);
  if (r.shipToRouting === "decorator") {
    rows.push(["Ship to", r.decoratorVendorName ? `${r.decoratorVendorName} (decorator)` : "Decorator"]);
  } else if (r.shipToRouting === "merchd") {
    rows.push(["Ship to", "Other warehouse"]);
  } else {
    rows.push(["Ship to", snap?.label?.trim() || (ROUTING_LABELS[r.shipToRouting] ?? r.shipToRouting)]);
    if (snap && (snap.street1 || snap.city)) rows.push(["Address", <AddressLines snap={snap} />]);
  }
  if (r.supplierShippingMethod) rows.push(["Method", r.supplierShippingMethod]);
  if (r.supplierNotes?.trim()) rows.push(["Notes", <span className="whitespace-pre-line">{r.supplierNotes.trim()}</span>]);
  return (
    <td className="px-3 py-3 align-top">
      <DetailRows rows={rows} />
    </td>
  );
}

function DecoratorDetail({ r }: { r: LineShippingRow }) {
  if (r.shipToRouting !== "decorator") {
    return <td className="px-3 py-3 align-top text-[14px] text-brown-300">No decorator</td>;
  }
  const rows: Array<[string, ReactNode]> = [];
  rows.push(["Name", r.decoratorVendorName ?? <span className="italic text-brown-300">Not set</span>]);
  if (r.decoratorShipDate) rows.push(["Ship date", shipDateText(r.decoratorShipDate, r.decoratorShipDateFirm)]);
  if (r.decoratorFinalShipTo) {
    rows.push(["Ship to", r.decoratorFinalShipTo === "merchd" ? "Other warehouse" : (ROUTING_LABELS[r.decoratorFinalShipTo] ?? r.decoratorFinalShipTo)]);
  }
  if (r.decoratorShippingMethod) rows.push(["Method", r.decoratorShippingMethod]);
  if (r.decoratorNotes?.trim()) rows.push(["Notes", <span className="whitespace-pre-line">{r.decoratorNotes.trim()}</span>]);
  return (
    <td className="px-3 py-3 align-top">
      <DetailRows rows={rows} />
    </td>
  );
}

export function OrderShippingTab(props: {
  orderId: string;
  clientId: string | null;
  /** Unlocks the editable table. Before Client Approved we explain-don't-hide. */
  unlocked: boolean;
  /** Anchor to the rep approval control (#so-approval), or null when none
   *  exists this stage — an upfront-payment SO is approved client-side (Approve
   *  & Pay), so there's no rep control to link to and the locked copy reads as
   *  a plain "awaiting client approval" instead (S5). */
  approveHref: string | null;
  /** Where "Create purchase orders →" points (the quote's #create-pos), or
   *  null when the Create-POs CTA doesn't render this stage — the same
   *  shouldShowNextAction stage set, so it never dangles on invoiced/closed
   *  orders (S4). */
  createPoHref: string | null;
  /** Item picture + logos per line id (the Shipping tab passes them). */
  lineArt?: Map<string, ShippingLineArt>;
  /** Rendered under the plan once it's unlocked (the project's Shipping tab
   *  puts Create purchase orders here). Gets the plan's staging state so the
   *  create step only appears once every item's shipping is filled in. */
  renderCreatePo?: (planRows: LineShippingRow[], selection: { ids: string[]; clear: () => void }) => ReactNode;
}) {
  const { orderId, clientId, unlocked, approveHref, createPoHref, renderCreatePo, lineArt } = props;
  const [editing, setEditing] = useState<LineShippingRow | null>(null);
  const [splitting, setSplitting] = useState<LineShippingRow | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ["orders", orderId, "line-shipping"],
    queryFn: () => api<LineShippingResponse>(`/orders/${orderId}/line-shipping`),
    enabled: unlocked,
  });

  const splitsQ = useQuery({
    queryKey: ["orders", orderId, "ship-splits"],
    queryFn: () => api<ShipSplitsResponse>(`/orders/${orderId}/ship-splits`),
    enabled: unlocked,
  });
  const splitsReady = splitsQ.data?.ready ?? false;
  const splitsFor = (lineId: string): SavedSplit[] => splitsQ.data?.splits[lineId] ?? [];

  const projectId = query.data?.projectId ?? null;
  const productRows = (query.data?.rows ?? []).filter(
    (r) => r.serviceType === "product",
  );
  const total = productRows.length;
  const stagedCount = productRows.filter((r) => r.hasStagedRow).length;
  const allStaged = total > 0 && stagedCount === total;
  // Coverage-aware footer (E3 coherence): lines NOT yet on a live PO are the ones
  // "Create purchase orders" would act on. In a MIXED state where every line is
  // already covered, the pointer would lie — gate it on there being uncovered
  // lines AND those uncovered lines being staged.
  const uncoveredRows = productRows.filter((r) => r.coveredByPoNumber == null);
  const coveredCount = total - uncoveredRows.length;
  const uncoveredAllStaged =
    uncoveredRows.length > 0 && uncoveredRows.every((r) => r.hasStagedRow);

  // Only uncovered product lines can be bulk-edited (covered lines' staging is
  // frozen). Keep the live selection pruned to selectable ids so a line that
  // becomes covered after a refetch can never linger in the batch.
  const selectableIds = useMemo(
    () =>
      productRows.filter(isLineBulkSelectable).map((r) => r.lineItemId),
    [productRows],
  );
  const selectedIds = useMemo(
    () => selectableIds.filter((id) => selected.has(id)),
    [selectableIds, selected],
  );
  const allSelected =
    selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  function toggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }
  function toggleOne(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section className="mb-6 rounded-lg border border-brown-100 bg-white">
      <div className="flex items-center justify-between border-b border-brown-50 px-5 py-3">
        <h2 className="inline-flex items-center gap-2 font-display text-lg font-bold text-brown-500">
          <ClipboardList className="h-4 w-4 text-muted" /> Shipping plan
        </h2>
      </div>

      {!unlocked ? (
        <div className="px-5 py-6">
          {approveHref ? (
            <p className="text-sm text-brown-400">
              Plan how each item ships to the client, a decorator, or Other
              once the client approves this Quote.{" "}
              <a
                href={approveHref}
                className="font-semibold text-ink hover:text-muted"
              >
                Approve it first
              </a>
              .
            </p>
          ) : (
            <p className="text-sm text-brown-400">
              Plan how each item ships to the client, a decorator, or Other
              once the client approves this Quote. It's awaiting client
              approval.
            </p>
          )}
        </div>
      ) : query.isLoading ? (
        <p className="px-5 py-6 text-sm text-brown-300">Loading shipping…</p>
      ) : total === 0 ? (
        <p className="px-5 py-6 text-sm text-brown-400">
          This order has no product lines to ship yet.
        </p>
      ) : (
        <>
          {/* Bulk action bar — disabled-with-reason when nothing is selected
              (house NextActionCta grammar: never a dead-looking primary). */}
          <div className="flex flex-wrap items-center gap-3 border-b border-brown-50 px-5 py-3">
            {/* D10 — outline/sm so this is a SECONDARY action (the Create-POs CTA
                is the page's primary); disabled-with-reason when empty. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
              disabled={selectedIds.length === 0}
              title={
                selectedIds.length === 0
                  ? "Select one or more items to edit their shipping together"
                  : undefined
              }
            >
              Edit Selected{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
            </Button>
            <span className="text-sm text-brown-300">
              {selectedIds.length === 0
                ? "Select items to set their shipping in one step."
                : `${selectedIds.length} selected`}
            </span>
          </div>
          <div className="overflow-x-auto px-2 py-2">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label="Select all items"
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      title={
                        selectableIds.length === 0
                          ? "No selectable items (all are covered by a PO)"
                          : undefined
                      }
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-brown-200 text-rust-400 focus:ring-rust-300"
                    />
                  </th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Supplier Shipping</th>
                  <th className="px-3 py-2">Decorator Shipping</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {productRows.map((r) => {
                  const isSelected = selected.has(r.lineItemId);
                  return (
                  <tr
                    key={r.lineItemId}
                    className={`border-t border-brown-50 ${
                      isSelected ? "bg-cream-50" : ""
                    }`}
                  >
                    <td className="px-3 py-2 align-top">
                      {isLineBulkSelectable(r) ? (
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.productSnapshot?.name ?? "item"}`}
                          checked={isSelected}
                          onChange={() => toggleOne(r.lineItemId)}
                          className="h-4 w-4 rounded border-brown-200 text-rust-400 focus:ring-rust-300"
                        />
                      ) : (
                        // Covered lines can't be re-staged — a DISABLED checkbox
                        // with the reason (D10), not an invisible gap.
                        <input
                          type="checkbox"
                          disabled
                          checked={false}
                          readOnly
                          aria-label={`${r.productSnapshot?.name ?? "Item"} is covered by a PO`}
                          title="Covered by a PO — cancel that PO to re-stage this line"
                          className="h-4 w-4 rounded border-brown-200 text-muted"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 align-top font-semibold text-brown-500">
                      {rowLabel(r)}
                      <LineArtStrip art={lineArt?.get(r.lineItemId)} />
                      {splitsFor(r.lineItemId).length > 0 && (
                        <span className="mt-0.5 block text-[12px] font-normal text-brown-400">
                          Split to {splitsFor(r.lineItemId).length} addresses: {splitSummary(splitsFor(r.lineItemId))}
                        </span>
                      )}
                    </td>
                    <SupplierDetail r={r} />
                    <DecoratorDetail r={r} />
                    <td className="px-3 py-2 align-top text-right">
                      {/* Edit + Split always (Stephanie 2026-10-27: "the shipping
                          tab should not lock at all"); a line on a PO also links to it. */}
                      <span className="inline-flex flex-wrap items-center justify-end gap-2">
                        {r.coveredByPoNumber && projectId && (
                          <Link
                            to={`/projects/${projectId}/pos/${r.coveredByPoId}`}
                            className="text-sm font-semibold text-ink hover:text-muted"
                          >
                            On PO #{r.coveredByPoNumber} →
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => setSplitting(r)}
                          disabled={!splitsReady}
                          title={splitsReady ? "Ship this item to more than one address" : "Split shipping is waiting on a database step from Jake"}
                          className="rounded-lg border border-brown-200 px-3 py-1.5 text-sm font-semibold text-brown-500 hover:bg-cream-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {splitsFor(r.lineItemId).length > 0 ? "Edit split" : "Split"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(r)}
                          className="rounded-lg border border-brown-200 px-3 py-1.5 text-sm font-semibold text-brown-500 hover:bg-cream-50"
                        >
                          Edit
                        </button>
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-brown-50 px-5 py-3 text-sm text-brown-400">
            <span>
              {stagedCount} of {total} lines staged
              {coveredCount > 0 ? ` · ${coveredCount} on a PO` : ""}.
            </span>
            {/* Only point to Create-POs when there's actually something to
                create (uncovered lines) AND those lines are staged (E3). */}
            {uncoveredRows.length > 0 &&
              uncoveredAllStaged &&
              createPoHref && (
                <a
                  href={createPoHref}
                  className="font-semibold text-ink hover:text-muted"
                >
                  Create purchase orders →
                </a>
              )}
          </div>
        </>
      )}

      {unlocked && !query.isLoading && renderCreatePo && total > 0 && (
        <div className="border-t border-brown-50 px-5 py-4">
          {renderCreatePo(productRows, { ids: selectedIds, clear: () => setSelected(new Set()) })}
        </div>
      )}

      {editing && clientId && (
        <LineItemShippingModal
          orderId={orderId}
          clientId={clientId}
          inHandsDate={query.data?.inHandsDate ?? null}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void query.refetch();
          }}
        />
      )}

      {splitting && clientId && (
        <ShipSplitModal
          orderId={orderId}
          clientId={clientId}
          lineItemId={splitting.lineItemId}
          itemLabel={rowLabel(splitting)}
          quantityTotal={splitsQ.data?.lines[splitting.lineItemId]?.quantityTotal ?? 0}
          variants={splitsQ.data?.lines[splitting.lineItemId]?.variants ?? []}
          saved={splitsFor(splitting.lineItemId)}
          onClose={() => setSplitting(null)}
          onSaved={() => {
            setSplitting(null);
            void splitsQ.refetch();
          }}
        />
      )}

      {bulkOpen && clientId && selectedIds.length > 0 && (
        <LineItemShippingModal
          orderId={orderId}
          clientId={clientId}
          inHandsDate={query.data?.inHandsDate ?? null}
          bulkLineIds={selectedIds}
          bulkSupplierVendorId={(() => {
            // Pre-pick the supplier when every selected item has the same one.
            const ids = new Set(
              productRows
                .filter((r) => selectedIds.includes(r.lineItemId))
                .map((r) => r.supplierVendorId ?? r.productSnapshot?.vendorId ?? ""),
            );
            const [only] = [...ids];
            return ids.size === 1 && only ? only : null;
          })()}
          bulkCommonRow={commonShippingRow(productRows.filter((r) => selectedIds.includes(r.lineItemId)))}
          bulkLabels={Object.fromEntries(
            productRows.map((r) => [r.lineItemId, rowLabel(r)]),
          )}
          onFixLine={(lineId) => {
            // D9 — open the single-line editor for a failed line.
            const target = productRows.find((r) => r.lineItemId === lineId);
            if (target) {
              setBulkOpen(false);
              setEditing(target);
            }
          }}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            setBulkOpen(false);
            setSelected(new Set());
            void query.refetch();
          }}
        />
      )}
    </section>
  );
}
